import React, { useState, useEffect } from 'react';
import { PawPrint, Users, Heart } from 'lucide-react';
import { getPatientStats, getTopCustomers } from '../../services/api';

const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const formatCurrency = (a) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(a);

function BarList({ items, colorClass = 'bg-[#6b6de3]' }) {
  const max = Math.max(...items.map(i => i.count), 1);
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] text-[#8E8E93] w-20 truncate text-right shrink-0">{item.name}</span>
          <div className="flex-1 bg-[#F2F2F7] rounded-full h-4 overflow-hidden">
            <div className={`h-full rounded-full ${colorClass} transition-all`} style={{ width: `${(item.count / max) * 100}%` }} />
          </div>
          <span className="text-[10px] font-bold text-gray-900 w-8 text-right">{item.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function PatientReport({ dateFrom, dateTo }) {
  const [stats, setStats] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPatientStats().then(r => setStats(r.data)).catch(() => {}),
      getTopCustomers(dateFrom, dateTo).then(r => setTopCustomers(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  if (loading) return <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>;
  if (!stats) return null;

  const neuteredRate = stats.cat_dog_count > 0 ? Math.round((stats.neutered_count / stats.cat_dog_count) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="ios-card">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#F2F2F7] rounded-xl p-3 text-center">
            <PawPrint size={18} className="text-[#FF9500] mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{stats.total_pets}</p>
            <p className="text-[10px] text-[#8E8E93]">Toplam Hasta</p>
          </div>
          <div className="bg-[#F2F2F7] rounded-xl p-3 text-center">
            <Users size={18} className="text-[#6b6de3] mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{stats.total_customers}</p>
            <p className="text-[10px] text-[#8E8E93]">Toplam Müşteri</p>
          </div>
        </div>
      </div>

      {/* Species Distribution */}
      {stats.species?.length > 0 && (
        <div className="ios-card">
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">Tür Dağılımı</p>
          <BarList items={stats.species} colorClass="bg-[#FF9500]" />
        </div>
      )}

      {/* Breed Distribution */}
      {stats.breeds?.length > 0 && (
        <div className="ios-card">
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">Irk Dağılımı (İlk 20)</p>
          <BarList items={stats.breeds.map(b => ({ name: b.breed, count: b.count }))} colorClass="bg-[#AF52DE]" />
        </div>
      )}

      {/* Gender Distribution */}
      {stats.genders?.length > 0 && (
        <div className="ios-card">
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">Cinsiyet Dağılımı</p>
          <BarList items={stats.genders} colorClass="bg-[#007AFF]" />
        </div>
      )}

      {/* Neutered Rate */}
      <div className="ios-card">
        <div className="flex items-center gap-2 mb-3">
          <Heart size={14} className="text-[#FF3B30]" />
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Kısırlaştırma Oranı</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#F2F2F7] rounded-full h-5 overflow-hidden">
            <div className="h-full rounded-full bg-[#FF3B30] transition-all" style={{ width: `${neuteredRate}%` }} />
          </div>
          <span className="text-sm font-bold text-gray-900">{`%${neuteredRate}`}</span>
        </div>
        <p className="text-[10px] text-[#8E8E93] mt-1">{stats.neutered_count} / {stats.cat_dog_count} (kedi + köpek)</p>
      </div>

      {/* Top 10 Customers */}
      {topCustomers?.length > 0 && (
        <div className="ios-card">
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">En Sık Gelen Müşteriler</p>
          <div className="space-y-1">
            {topCustomers.map((c, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#F2F2F7] rounded-xl">
                <span className="w-5 h-5 rounded-full bg-[#6b6de3]/10 text-[#6b6de3] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{c.name}</p>
                  {c.phone && <p className="text-[10px] text-[#8E8E93]">{c.phone}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold text-[#FF9500]">{formatCurrency(c.total)}</p>
                  <p className="text-[9px] text-[#8E8E93]">{c.count} işlem</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Registrations */}
      {(stats.monthly_pets?.length > 0 || stats.monthly_customers?.length > 0) && (
        <div className="ios-card">
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">Aylık Yeni Kayıtlar</p>
          <div className="flex items-end gap-1" style={{ height: '80px' }}>
            {stats.monthly_pets.map((m, i) => {
              const max = Math.max(...stats.monthly_pets.map(x => x.count), 1);
              const h = Math.max((m.count / max) * 100, 4);
              const monthNum = parseInt(m.month.split('-')[1], 10);
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  {m.count > 0 && <span className="text-[8px] text-[#8E8E93] mb-0.5">{m.count}</span>}
                  <div className="w-full max-w-[24px] bg-[#FF9500] rounded-t" style={{ height: `${h}%` }} />
                  <span className="text-[8px] text-[#8E8E93] mt-1">{MONTHS_SHORT[monthNum - 1]}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-2 justify-center">
            <span className="flex items-center gap-1 text-[9px] text-[#8E8E93]"><span className="w-2 h-2 rounded bg-[#FF9500]" /> Hasta</span>
          </div>
        </div>
      )}
    </div>
  );
}
